import { Button } from '@helix-ds/react'
import { AlertBanner } from '@/components/common/AlertBanner'
import { Modal, SaveIndicator, TextareaInput } from '@/components/common/Modal'
import { useAutosave } from '@/components/common/useAutosave'
import type { FamilyHistorySummaryData } from '@/components/order/OrderTypes'
import {
  CLINICAL_DESCRIPTION_MAX_LENGTH,
  type ClinicalTextBudget,
  combinedClinicalDescriptionLength,
  isValidFreeText,
  RCIGM_COMBINED_CLINICAL_TEXT_BUDGET,
  SHARED_CLINICAL_TEXT_HELPER,
  SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE,
} from '@/utils/freeTextValidation'
import { ArrowRight } from 'flowbite-react-icons/outline'
import { useEffect, useRef, useState } from 'react'

interface FamilyHistorySummaryModalProps {
  isOpen: boolean
  onClose: () => void
  initialData: FamilyHistorySummaryData
  onSave: (data: FamilyHistorySummaryData) => void
  /**
   * R5: when provided, renders the brand "Continue to …" footer CTA (label from
   * `continueLabel`). The parent persists family history and advances to the
   * next section; "Done" demotes to the secondary style so there is a single
   * primary action.
   */
  onContinue?: (data: FamilyHistorySummaryData) => void
  continueLabel?: string
  /** Clinical note from canonical, included in the combined clinical `description` budget. */
  clinicalNote?: string
  /** Differential diagnosis from canonical, included in the combined clinical `description` budget. */
  differentialDiagnosis?: string
  /** Prior genetic testing from canonical, included in the combined clinical `description` budget. */
  priorGeneticTesting?: string
  /**
   * Rady publishes these fields as one CareBus description capped at 10,000 characters;
   * other labs share one order-wide pool with every comparator summary.
   */
  clinicalTextBudget?: ClinicalTextBudget
}

/** R5: family history is optional, so `includeFamilyHistory` follows the summary
 * content rather than a manual toggle — empty summary collapses it to false. */
function withDerivedInclude(data: FamilyHistorySummaryData): FamilyHistorySummaryData {
  return { ...data, includeFamilyHistory: (data.summary ?? '').trim().length > 0 }
}

export function FamilyHistorySummaryModal({
  isOpen,
  onClose,
  initialData,
  onSave,
  onContinue,
  continueLabel = 'Continue',
  clinicalNote,
  differentialDiagnosis,
  priorGeneticTesting,
  clinicalTextBudget = RCIGM_COMBINED_CLINICAL_TEXT_BUDGET,
}: FamilyHistorySummaryModalProps) {
  const [data, setData] = useState<FamilyHistorySummaryData>(initialData)
  const { lastSaveTime, isSaving } = useAutosave(data, onSave, 500, isOpen)

  // Keep the latest `initialData` on a ref so we can hydrate on open without
  // resubscribing to its identity. Autosave dispatches merge* actions while
  // the modal is open, which changes the canonical-derived `initialData`
  // reference on every keystroke; re-hydrating on that would fight the user's
  // in-progress edits.
  const initialDataRef = useRef(initialData)
  initialDataRef.current = initialData
  // Snapshot captured when the modal opens so Discard reverts to pre-edit
  // state even after autosave has already mutated canonical mid-session.
  const openBaselineRef = useRef<FamilyHistorySummaryData>(initialData)

  useEffect(() => {
    if (isOpen) {
      openBaselineRef.current = initialDataRef.current
      setData(withDerivedInclude(initialDataRef.current))
    }
  }, [isOpen])

  const handleClose = () => {
    onClose()
  }

  const handleContinue = () => {
    onContinue?.(data)
    onClose()
  }

  const handleDiscard = () => {
    onSave(openBaselineRef.current)
    onClose()
  }

  const handleSummaryChange = (value: string) => {
    setData((prev) => withDerivedInclude({ ...prev, summary: value, hasInteracted: true }))
  }

  const combinedLength =
    combinedClinicalDescriptionLength(
      clinicalNote ?? '',
      data.includeFamilyHistory ? data.summary : '',
      differentialDiagnosis ?? '',
      priorGeneticTesting ?? ''
    ) + clinicalTextBudget.usedElsewhere
  const overCombinedLimit = combinedLength > clinicalTextBudget.limit
  const isShared = clinicalTextBudget.kind === 'shared'
  const doneDisabled = isSaving || !isValidFreeText(data.summary) || overCombinedLimit

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Family History"
      size="lg"
      footer={
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <kbd className="rounded-sm border border-border-base-medium bg-background-tertiary px-2 py-1.5 font-sans text-xs font-normal text-text-body dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              esc
            </kbd>
            <span className="text-xs text-text-body-subtle dark:text-gray-500">Save & Close</span>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
            <SaveIndicator lastSaveTime={lastSaveTime} isSaving={isSaving} />
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={handleDiscard}
                className="rounded-full border border-border-base bg-white px-4 py-2.5 text-sm font-medium text-text-heading shadow-xs transition-colors hover:bg-background-gray dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
              >
                Discard Changes
              </button>
              {!onContinue && (
                <Button
                  variant="primary"
                  size="md"
                  type="button"
                  disabled={doneDisabled}
                  onClick={handleClose}
                  className="focus-visible:ring-brand-600"
                >
                  Done
                </Button>
              )}
              {onContinue && (
                <Button
                  variant="primary"
                  size="md"
                  type="button"
                  disabled={doneDisabled}
                  onClick={handleContinue}
                  className="focus-visible:ring-brand-600"
                >
                  {continueLabel}
                  <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                </Button>
              )}
            </div>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-6 sm:py-8">
        <AlertBanner useInflectionIcon={true}>
          Sharing pertinent family history provides context that helps the lab interpret candidate
          variants.
        </AlertBanner>

        {overCombinedLimit && (
          <AlertBanner variant="error">
            {isShared ? (
              SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE
            ) : (
              <>
                The combined clinical summary, differential diagnosis, prior genetic testing, and
                family history exceed the {CLINICAL_DESCRIPTION_MAX_LENGTH.toLocaleString()}-character
                limit. Please shorten your entries before continuing.
              </>
            )}
          </AlertBanner>
        )}

        <TextareaInput
          value={data.summary}
          onChange={handleSummaryChange}
          placeholder="Enter summary of family history..."
          rows={6}
          className="min-h-[188px] resize-y border-border-base-medium bg-background-secondary-medium py-3.5 shadow-xs dark:border-gray-700 dark:bg-gray-800/80"
          helperText={
            isShared
              ? SHARED_CLINICAL_TEXT_HELPER
              : 'The counter reflects the combined clinical summary, differential diagnoses, and family history.'
          }
          characterCount={combinedLength}
          characterLimit={clinicalTextBudget.limit}
          characterCountOverLimit={overCombinedLimit}
        />
      </div>
    </Modal>
  )
}
